import { wbHuelleAlle, wbTextKasten, type WbElement, type WbFelder } from '@beholder/shared/module/whiteboard';

export interface Punkt {
  x: number;
  y: number;
}

export interface Ansicht {
  x: number;
  y: number;
  zoom: number;
}

/** Gedrehter Kasten: Mitte, Größe, Drehung in Grad. */
export interface DrehKasten {
  cx: number;
  cy: number;
  b: number;
  h: number;
  w: number;
}

export const rad = (g: number) => (g * Math.PI) / 180;

export function drehen(p: Punkt, grad: number): Punkt {
  const w = rad(grad);
  const c = Math.cos(w);
  const s = Math.sin(w);
  return { x: p.x * c - p.y * s, y: p.x * s + p.y * c };
}

export function mitte(e: Pick<WbElement, 'x' | 'y' | 'b' | 'h'>): Punkt {
  return { x: e.x + e.b / 2, y: e.y + e.h / 2 };
}

/** Kasten um die Auswahl: ein Element = sein gedrehter Kasten, mehrere = achsparallel. */
export function auswahlKasten(els: WbElement[]): DrehKasten | null {
  if (!els.length) return null;
  if (els.length === 1) {
    const e = els[0]!;
    return { cx: e.x + e.b / 2, cy: e.y + e.h / 2, b: e.b, h: e.h, w: e.drehung };
  }
  const k = wbHuelleAlle(els)!;
  return { cx: k.x + k.b / 2, cy: k.y + k.h / 2, b: k.b, h: k.h, w: 0 };
}

/** Ecke/Kante eines gedrehten Kastens in Weltkoordinaten (sx, sy ∈ −1, 0, 1). */
export function kastenPunkt(k: DrehKasten, sx: number, sy: number): Punkt {
  const p = drehen({ x: (sx * k.b) / 2, y: (sy * k.h) / 2 }, k.w);
  return { x: k.cx + p.x, y: k.cy + p.y };
}

type Lage = Pick<WbElement, 'x' | 'y' | 'b' | 'h' | 'drehung' | 'punkte' | 'schrift' | 'art' | 'text'>;

/**
 * Größe ändern: Griff (sx, sy) wird zum Punkt p gezogen, die gegenüberliegende Seite bleibt stehen.
 * Liefert neue Felder je Element.
 */
export function skalieren(
  k0: DrehKasten,
  sx: number,
  sy: number,
  p: Punkt,
  els: Map<string, Lage>,
  seitenFest: boolean,
): Map<string, WbFelder> {
  const anker = kastenPunkt(k0, -sx, -sy);
  const l = drehen({ x: p.x - anker.x, y: p.y - anker.y }, -k0.w);
  let b1 = sx ? Math.max(2, sx * l.x) : k0.b;
  let h1 = sy ? Math.max(2, sy * l.y) : k0.h;
  if (seitenFest && sx && sy && k0.b > 0 && k0.h > 0) {
    const f = Math.max(b1 / k0.b, h1 / k0.h);
    b1 = k0.b * f;
    h1 = k0.h * f;
  }
  const fx = k0.b > 0 ? b1 / k0.b : 1;
  const fy = k0.h > 0 ? h1 / k0.h : 1;
  const versatz = drehen({ x: (sx * b1) / 2, y: (sy * h1) / 2 }, k0.w);
  const c1 = { x: anker.x + versatz.x, y: anker.y + versatz.y };
  const aus = new Map<string, WbFelder>();
  for (const [id, e] of els) {
    const c = mitte(e);
    const rel = drehen({ x: c.x - k0.cx, y: c.y - k0.cy }, -k0.w);
    const neu = drehen({ x: rel.x * fx, y: rel.y * fy }, k0.w);
    const nc = { x: c1.x + neu.x, y: c1.y + neu.y };
    let b = e.b * fx;
    let h = e.h * fy;
    const f: WbFelder = {};
    if (e.art === 'text') {
      f.schrift = Math.min(400, Math.max(4, e.schrift * (sy ? fy : fx)));
      const tk = wbTextKasten(e.text, f.schrift);
      b = tk.b;
      h = tk.h;
    }
    if (e.punkte) f.punkte = e.punkte.map((v, i) => (i % 2 === 0 ? v * fx : v * fy));
    f.x = nc.x - b / 2;
    f.y = nc.y - h / 2;
    f.b = b;
    f.h = h;
    aus.set(id, f);
  }
  return aus;
}

/** Drehen um die Mitte des Auswahlkastens. */
export function drehenUm(k0: DrehKasten, grad: number, els: Map<string, Lage>): Map<string, WbFelder> {
  const aus = new Map<string, WbFelder>();
  for (const [id, e] of els) {
    const c = mitte(e);
    const r = drehen({ x: c.x - k0.cx, y: c.y - k0.cy }, grad);
    let d = (e.drehung + grad) % 360;
    if (d < 0) d += 360;
    aus.set(id, { x: k0.cx + r.x - e.b / 2, y: k0.cy + r.y - e.h / 2, drehung: d });
  }
  return aus;
}

/** Punkte (absolut, flach) → Kasten + relative Punkte. */
export function punkteNormieren(abs: number[]): Pick<WbElement, 'x' | 'y' | 'b' | 'h' | 'punkte'> {
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  for (let i = 0; i < abs.length; i += 2) {
    x1 = Math.min(x1, abs[i]!);
    x2 = Math.max(x2, abs[i]!);
    y1 = Math.min(y1, abs[i + 1]!);
    y2 = Math.max(y2, abs[i + 1]!);
  }
  const punkte = abs.map((v, i) => Math.round((v - (i % 2 === 0 ? x1 : y1)) * 100) / 100);
  return { x: x1, y: y1, b: Math.max(1, x2 - x1), h: Math.max(1, y2 - y1), punkte };
}

/** Linienpunkte eines Elements in Weltkoordinaten (ohne Drehung). */
export function absolutePunkte(e: Pick<WbElement, 'x' | 'y' | 'punkte'>): number[] {
  return (e.punkte ?? []).map((v, i) => v + (i % 2 === 0 ? e.x : e.y));
}

/** Strich vereinfachen (Ramer-Douglas-Peucker), damit weniger Punkte gespeichert werden. */
export function vereinfachen(p: number[], toleranz: number): number[] {
  const n = p.length / 2;
  if (n <= 2) return p;
  const behalten = new Uint8Array(n);
  behalten[0] = 1;
  behalten[n - 1] = 1;
  const stapel: [number, number][] = [[0, n - 1]];
  while (stapel.length) {
    const [a, b] = stapel.pop()!;
    const ax = p[a * 2]!, ay = p[a * 2 + 1]!, bx = p[b * 2]!, by = p[b * 2 + 1]!;
    const dx = bx - ax, dy = by - ay;
    const l = Math.hypot(dx, dy) || 1;
    let max = 0, idx = -1;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs((p[i * 2]! - ax) * dy - (p[i * 2 + 1]! - ay) * dx) / l;
      if (d > max) {
        max = d;
        idx = i;
      }
    }
    if (max > toleranz && idx > 0) {
      behalten[idx] = 1;
      stapel.push([a, idx], [idx, b]);
    }
  }
  const aus: number[] = [];
  for (let i = 0; i < n; i++) if (behalten[i]) aus.push(p[i * 2]!, p[i * 2 + 1]!);
  return aus;
}

/** Schneiden sich zwei achsparallele Kästen (bzw. liegt a ganz in b)? */
export function kastenIn(a: { x: number; y: number; b: number; h: number }, b: { x: number; y: number; b: number; h: number }, ganz: boolean) {
  if (ganz) return a.x >= b.x && a.y >= b.y && a.x + a.b <= b.x + b.b && a.y + a.h <= b.y + b.h;
  return a.x <= b.x + b.b && a.x + a.b >= b.x && a.y <= b.y + b.h && a.y + a.h >= b.y;
}

// ---------------------------------------------------------------- Haftende Pfeile

/** Linienenden eines Elements in Weltkoordinaten (mit Drehung). */
export function endpunkteWelt(el: Pick<WbElement, 'x' | 'y' | 'b' | 'h' | 'drehung' | 'punkte'>): [Punkt, Punkt] {
  const pk = el.punkte ?? [0, 0, el.b, el.h];
  const c = { x: el.b / 2, y: el.h / 2 };
  const f = (x: number, y: number) => {
    const r = drehen({ x: x - c.x, y: y - c.y }, el.drehung);
    return { x: el.x + c.x + r.x, y: el.y + c.y + r.y };
  };
  return [f(pk[0] ?? 0, pk[1] ?? 0), f(pk[2] ?? 0, pk[3] ?? 0)];
}

/** Elemente, an denen Pfeile haften können. */
export function kannHalten(e: WbElement) {
  return !e.geloescht && e.art !== 'stift' && e.art !== 'linie' && e.art !== 'pfeil';
}

/** Liegt der Punkt in (oder knapp an) dem gedrehten Kasten des Elements? */
export function punktInElement(e: WbElement, p: Punkt, rand = 0): boolean {
  const c = mitte(e);
  const l = drehen({ x: p.x - c.x, y: p.y - c.y }, -e.drehung);
  return Math.abs(l.x) <= e.b / 2 + rand && Math.abs(l.y) <= e.h / 2 + rand;
}

/** Punkt auf dem Rand des Elements in Richtung `ziel` (mit kleinem Abstand). */
export function randPunkt(e: WbElement, ziel: Punkt, abstand = 6): Punkt {
  const c = mitte(e);
  const l = drehen({ x: ziel.x - c.x, y: ziel.y - c.y }, -e.drehung);
  const hw = Math.max(1, e.b / 2);
  const hh = Math.max(1, e.h / 2);
  const laenge = Math.hypot(l.x, l.y);
  if (laenge < 1e-6) return c;
  let t: number;
  if (e.art === 'ellipse') t = 1 / Math.sqrt((l.x / hw) ** 2 + (l.y / hh) ** 2);
  else if (e.art === 'raute') t = 1 / (Math.abs(l.x) / hw + Math.abs(l.y) / hh);
  else t = Math.min(l.x ? hw / Math.abs(l.x) : Infinity, l.y ? hh / Math.abs(l.y) : Infinity);
  const f = Math.min(1, t + abstand / laenge);
  const r = drehen({ x: l.x * f, y: l.y * f }, e.drehung);
  return { x: c.x + r.x, y: c.y + r.y };
}

/** Oberstes Element unter einem Punkt, an dem ein Pfeil haften kann. */
export function halterUnter(sortiert: WbElement[], p: Punkt, rand: number, ausser?: string): WbElement | null {
  for (let i = sortiert.length - 1; i >= 0; i--) {
    const e = sortiert[i]!;
    if (e.id !== ausser && kannHalten(e) && punktInElement(e, p, rand)) return e;
  }
  return null;
}

/** Linienpunkte aus zwei Enden, an haltende Elemente angelegt. */
export function pfeilLage(
  a: Punkt,
  b: Punkt,
  start: WbElement | null,
  ende: WbElement | null,
): Pick<WbElement, 'x' | 'y' | 'b' | 'h' | 'punkte' | 'drehung'> {
  const zielA = ende ? mitte(ende) : b;
  const zielB = start ? mitte(start) : a;
  const p0 = start ? randPunkt(start, zielA) : a;
  const p1 = ende ? randPunkt(ende, zielB) : b;
  return { ...punkteNormieren([p0.x, p0.y, p1.x, p1.y]), drehung: 0 };
}

/**
 * Pfeile, deren Anfang oder Ende an einem geänderten Element haftet, mitziehen.
 * `holen` liefert den aktuellen Stand, `geaendert` die neuen Felder der bewegten Elemente.
 */
export function pfeileNachziehen(
  sichtbar: WbElement[],
  geaendert: Map<string, WbFelder>,
  holen: (id: string) => WbElement | undefined,
): Map<string, WbFelder> {
  const aus = new Map<string, WbFelder>();
  const neu = (id: string | null): WbElement | null => {
    if (!id) return null;
    const e = holen(id);
    if (!e || e.geloescht) return null;
    const f = geaendert.get(id);
    return f ? { ...e, ...f } : e;
  };
  for (const p of sichtbar) {
    if ((p.art !== 'linie' && p.art !== 'pfeil') || geaendert.has(p.id)) continue;
    if (!(p.start && geaendert.has(p.start)) && !(p.ende && geaendert.has(p.ende))) continue;
    const [a, b] = endpunkteWelt(p);
    aus.set(p.id, pfeilLage(a, b, neu(p.start), neu(p.ende)));
  }
  return aus;
}

// ---------------------------------------------------------------- Ebenen

/**
 * Ausgewählte Elemente eine Ebene vor oder zurück (an den Nachbarn vorbei).
 * Liefert neue z-Werte; die anderen Elemente behalten ihre Ebene.
 */
export function ebeneSchritt(sortiert: WbElement[], auswahl: Set<string>, richtung: 1 | -1): Map<string, number> {
  const ordnung = sortiert.map((e) => e.id);
  if (richtung === 1) {
    for (let i = ordnung.length - 2; i >= 0; i--) {
      if (auswahl.has(ordnung[i]!) && !auswahl.has(ordnung[i + 1]!)) [ordnung[i], ordnung[i + 1]] = [ordnung[i + 1]!, ordnung[i]!];
    }
  } else {
    for (let i = 1; i < ordnung.length; i++) {
      if (auswahl.has(ordnung[i]!) && !auswahl.has(ordnung[i - 1]!)) [ordnung[i], ordnung[i - 1]] = [ordnung[i - 1]!, ordnung[i]!];
    }
  }
  const zAlt = new Map(sortiert.map((e) => [e.id, e.z]));
  const aus = new Map<string, number>();
  // Läufe ausgewählter Elemente gleichmäßig zwischen die nicht ausgewählten Nachbarn legen
  let i = 0;
  while (i < ordnung.length) {
    if (!auswahl.has(ordnung[i]!)) {
      i++;
      continue;
    }
    let j = i;
    while (j < ordnung.length && auswahl.has(ordnung[j]!)) j++;
    const anzahl = j - i;
    const lo = i > 0 ? zAlt.get(ordnung[i - 1]!)! : null;
    const hi = j < ordnung.length ? zAlt.get(ordnung[j]!)! : null;
    const unten = lo ?? (hi !== null ? hi - anzahl - 1 : 0);
    const oben = hi ?? unten + anzahl + 1;
    for (let k = 0; k < anzahl; k++) aus.set(ordnung[i + k]!, unten + ((oben - unten) * (k + 1)) / (anzahl + 1));
    i = j;
  }
  for (const [id, z] of [...aus]) if (zAlt.get(id) === z) aus.delete(id);
  return aus;
}
